/**
 * Guards React against DOM mutations made by browser translation tools
 * (Chrome / Google "Translate to English", Edge translate, etc.).
 *
 * Those tools replace text nodes and wrap them in <font> elements, which
 * reparents nodes that React still tracks in its fiber tree. When React later
 * commits a deletion it calls parent.removeChild(node) (or insertBefore) on a
 * node that the translator has moved, throwing:
 *
 *   NotFoundError: Failed to execute 'removeChild' on 'Node':
 *   The node to be removed is not a child of this node.
 *
 * React assumes it owns the DOM and cannot recover from this on its own, so we
 * make removeChild/insertBefore no-op gracefully when the node is no longer a
 * child of the expected parent. This is the React team's recommended workaround
 * (facebook/react#11538) and keeps the app usable while translation is active.
 *
 * Must run before React renders. Import it first in main.tsx.
 */
export function installTranslationDomGuard(): void {
  if (typeof Node !== 'function' || !Node.prototype) return
  // Avoid double-patching (e.g. Vite HMR re-running the module).
  if ((Node.prototype as { __translationGuarded?: boolean }).__translationGuarded) return
  ;(Node.prototype as { __translationGuarded?: boolean }).__translationGuarded = true

  const originalRemoveChild = Node.prototype.removeChild
  Node.prototype.removeChild = function <T extends Node>(this: Node, child: T): T {
    if (child.parentNode !== this) {
      // Translation reparented this node; React's view is stale. No-op instead
      // of throwing so the reconciler can continue.
      return child
    }
    return originalRemoveChild.call(this, child) as T
  }

  const originalInsertBefore = Node.prototype.insertBefore
  Node.prototype.insertBefore = function <T extends Node>(
    this: Node,
    newNode: T,
    referenceNode: Node | null,
  ): T {
    if (referenceNode && referenceNode.parentNode !== this) {
      // Reference node was reparented by the translator; append instead of
      // throwing so React can complete the commit.
      return originalInsertBefore.call(this, newNode, null) as T
    }
    return originalInsertBefore.call(this, newNode, referenceNode) as T
  }
}
